import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Select, Input, Space, Typography, Breadcrumb,  } from 'antd';
import { showDeleteConfirm } from '../components/PopupModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { GreetingItem, GreetingFilterOptions, getExposureDisplayText } from '../types/greeting';
import greetingService from '../services/greeting';
import { message } from '../utils/message';

const { Title, Text } = Typography;
const { Option } = Select;

const GreetingManagement: React.FC = () => {
  const navigate = useNavigate();

  // 상태 관리
  const [greetingData, setGreetingData] = useState<GreetingItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<GreetingFilterOptions>({
    exposureType: 'all',
    searchKeyword: '',
    page: 1,
    limit: 10,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // 테이블 컬럼 정의
  const columns: ColumnsType<GreetingItem> = [
    {
      title: 'No.',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
      render: (_, __, index) => (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: '노출여부',
      dataIndex: 'exposureType',
      key: 'exposureType',
      width: 120,
      align: 'center',
      render: (exposureType: string) => (
        <Text style={{ color: '#ffffff' }}>
          {getExposureDisplayText(exposureType as any)}
        </Text>
      ),
    },
    {
      title: '제목',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text: string, record: GreetingItem) => (
        <Text
          style={{ color: '#ffffff', cursor: 'pointer' }}
          onClick={() => navigate(`/greeting-detail/${record.id}`)}
        >
          {text}
        </Text>
      ),
    },
    {
      title: '관리자명',
      dataIndex: 'creator',
      key: 'creator',
      width: 150,
      align: 'center',
      render: (creator: any, record: GreetingItem) => (
        <Text style={{ color: '#ffffff' }}>
          {creator?.name || creator?.email || 'admin'}
        </Text>
      ),
    },
    {
      title: '등록일',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      align: 'center',
      render: (date: string) => (
        <Text style={{ color: '#ffffff' }}>
          {new Date(date).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '.')}
        </Text>
      ),
    },
  ];

  // 데이터 로드 함수
  const loadGreetingData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await greetingService.getGreetingList(filters);
      setGreetingData(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('그리팅 목록 조회 실패:', error);
      message.error('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadGreetingData();
  }, [loadGreetingData]);

  // 필터 변경 핸들러
  const handleFilterChange = (key: keyof GreetingFilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value, // 페이지가 아닌 다른 필터 변경 시 1페이지로 이동
    }));
  };

  // 검색 핸들러
  const handleSearch = (value: string) => {
    handleFilterChange('searchKeyword', value);
  };

  // 페이지네이션 핸들러
  const handlePageChange = (page: number, pageSize?: number) => {
    setFilters(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit!,
    }));
  };



  // 체크박스 선택 핸들러
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };


  // 일괄 노출/비노출 처리
  const handleBulkExposure = async (exposureType: 'show' | 'hide') => {
    if (selectedRowKeys.length === 0) {
      message.warning('선택된 항목이 없습니다.');
      return;
    }

    try {
      await greetingService.bulkToggleExposure(
        selectedRowKeys.map(key => Number(key)),
        exposureType
      );
      message.success(getExposureDisplayText(exposureType) === "노출" ? "노출 처리 되었습니다." : "비노출 처리 되었습니다.");
      setSelectedRowKeys([]);
      loadGreetingData();
    } catch (error) {
      console.error('일괄 노출 상태 변경 실패:', error);
      message.error('노출 상태 변경 중 오류가 발생했습니다.');
    }
  };

  // 일궄 삭제
  const handleBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('선택된 항목이 없습니다.');
      return;
    }

    showDeleteConfirm({
      itemCount: selectedRowKeys.length,
      itemName: '그리팅',
      onConfirm: handleConfirmDelete,
    });
  };

  // 삭제 확인 처리
  const handleConfirmDelete = async () => {
    try {
      await greetingService.bulkDeleteGreetings(selectedRowKeys.map(key => Number(key)));
      message.success('삭제가 완료되었습니다.');
      setSelectedRowKeys([]);
      loadGreetingData();
    } catch (error) {
      console.error('일괄 삭제 실패:', error);
      message.error('그리팅 삭제 중 오류가 발생했습니다.');
    }
  };



  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '그리팅' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        그리팅 관리
      </Title>

      {/* 필터 및 검색 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>노출여부</Text>
            <Select
              value={filters.exposureType === 'all' ? '전체' : filters.exposureType === 'show' ? '노출' : '비노출'}
              onChange={(value) => {
                const exposureValue = value === '전체' ? 'all' : value === '노출' ? 'show' : 'hide';
                handleFilterChange('exposureType', exposureValue);
              }}
              style={{ width: 120 }}
            >
              <Option value="전체">전체</Option>
              <Option value="노출">노출</Option>
              <Option value="비노출">비노출</Option>
            </Select>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/greeting-add')}
        >
          추가
        </Button>
      </div>

      {/* 구분선 */}
      <div style={{
        height: '1px',
        backgroundColor: '#303030',
        margin: '24px 0'
      }} />

      {/* 테이블 상단 정보 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Text>총 {pagination.total}건</Text>
          <Button
            onClick={handleBulkDelete}
            disabled={selectedRowKeys.length === 0}
            danger
            size="middle"
          >
            삭제
          </Button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            onClick={() => handleBulkExposure('hide')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            비노출
          </Button>
          <Button
            onClick={() => handleBulkExposure('show')}
            disabled={selectedRowKeys.length === 0}
            size="middle"
          >
            노출
          </Button>
          <Input
            placeholder="검색"
            suffix={<SearchOutlined style={{ color: '#a6a6a6' }} />}
            style={{
              width: 400,
              height: 40,
              borderRadius: 8,
              background: '#2a2a2a',
              border: '1px solid #404040',
              color: '#ffffff'
            }}
            onChange={(e) => setFilters(prev => ({ ...prev, searchKeyword: e.target.value }))}
            onPressEnter={() => handleSearch(filters.searchKeyword || '')}
            value={filters.searchKeyword}
            allowClear
          />
        </div>
      </div>

      {/* 테이블 */}
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={greetingData}
        rowKey="id"
        loading={loading}
        onRow={(record) => ({
          onClick: () => {
            navigate(`/greeting-detail/${record.id}`);
          },
          style: { cursor: 'pointer' }
        })}
        pagination={{
          current: pagination.page,
          total: pagination.total,
          pageSize: pagination.limit,
          onChange: handlePageChange,
          showSizeChanger: true,
          showQuickJumper: false,
          pageSizeOptions: ['10', '20', '50'],
          style: { textAlign: 'center' },
        }}
        style={{
          backgroundColor: '#1f1f1f',
        }}
        size="middle"
        locale={{
          emptyText: loading ? '로딩 중...' : '데이터가 없습니다.'
        }}
      />

    </Space>
  );
};

export default GreetingManagement;